import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useSession } from '../auth/useSession';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function Login() {
  const { user } = useSession();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  // Redirect if already authenticated
  if (user) {
    const from = location.state?.from?.pathname || '/';
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    setMessage(null);

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: data.email,
        options: {
          emailRedirectTo: window.location.origin,
        },
      });

      if (error) {
        setMessage({ type: 'error', text: error.message });
      } else {
        setMessage({
          type: 'success',
          text: 'Check your email for the login link!',
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'An unexpected error occurred. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mx-auto mb-4">
            <Icon name="Shield" size={32} className="text-primary" />
          </div>
          <h2 className="text-3xl font-bold text-foreground">Welcome to PolyNotary</h2>
          <p className="mt-2 text-muted-foreground">
            Sign in to your account to start notarizing documents
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Input
              label="Email address"
              type="email"
              placeholder="Enter your email"
              {...register('email')}
              error={errors.email?.message}
              required
            />

            {message && (
              <div
                className={`p-4 rounded-md ${
                  message.type === 'success'
                    ? 'bg-success/10 border border-success/20 text-success'
                    : 'bg-destructive/10 border border-destructive/20 text-destructive'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Icon
                    name={message.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
                    size={16}
                  />
                  <p className="text-sm font-medium">{message.text}</p>
                </div>
              </div>
            )}

            <Button
              type="submit"
              fullWidth
              loading={isLoading}
              iconName="Mail"
              iconPosition="left"
            >
              {isLoading ? 'Sending...' : 'Send Magic Link'}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-border">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                We'll send you a secure login link via email.
                <br />
                No passwords required!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}